import { FC } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Anchor, Group, Text, Tooltip, useMantineTheme } from "@mantine/core";

import { Issue, IssueType } from "@protos/issues/v1/issues";
import { HoverCopyButton, SeverityCardTimestamp } from "@shared/components";
import { validateUrl } from "@shared/utils";

import { InfoIcon } from "../../types/infoIcon";

import { generateSastIcons } from "./generateSastIcons";
import { generateSecretsIcons } from "./generateSecretsIcons";

export interface BaseFindingInfoIconListProps {
  finding: Issue;
  children?: React.ReactNode;
}

const ICON_LIST_MAP: Record<IssueType, (finding: Issue) => InfoIcon[]> = {
  [IssueType.SAST]: generateSastIcons,
  [IssueType.SCA]: () => [],
  [IssueType.SECRETS]: generateSecretsIcons,
  [IssueType.RESEARCH]: () => [],
  [IssueType.UNSPECIFIED]: () => [],
};

export const FindingInfoIconList: FC<BaseFindingInfoIconListProps> = ({
  finding,
}) => {
  const theme = useMantineTheme();
  const textColor = theme.colors["gray"][8];

  const iconList = ICON_LIST_MAP[finding.issueType](finding);

  return (
    <Group gap="lg" style={{ rowGap: 8 }}>
      {finding.relevantSince && (
        <SeverityCardTimestamp
          date={finding.relevantSince}
          hasComments={false}
          isBlocking={finding.isBlocking ?? false}
          fontSize="sm"
          color="dark.4"
          tooltipWithArrow={false}
          tooltipPosition="bottom"
        />
      )}
      {iconList?.map(
        (
          {
            icon,
            label,
            href,
            copyText,
            copyTooltipText,
            onHoverText,
            branches,
            color,
            iconProps,
          },
          idx
        ) =>
          label !== "" && (
            <HoverCopyButton
              key={idx}
              copyText={copyText}
              tooltipText={copyTooltipText}
              iconSize="sm"
            >
              <Tooltip
                position="bottom"
                multiline
                maw={400}
                disabled={
                  !onHoverText || (branches ? branches.length === 0 : false)
                }
                label={
                  branches !== undefined ? (
                    <>
                      <Text fw={600}>Seen on:</Text>
                      <Text>{label.slice(0, -3)}</Text>
                      {branches.map((b, idx) => (
                        <Text key={idx}>{b}</Text>
                      ))}
                    </>
                  ) : (
                    onHoverText && (
                      <>
                        {onHoverText
                          .filter((t) => t !== "")
                          .map((t) => (
                            <Text inherit key={t}>
                              {t}
                            </Text>
                          ))}
                      </>
                    )
                  )
                }
              >
                <Group gap={8} wrap="nowrap" fz="sm">
                  <FontAwesomeIcon
                    icon={icon}
                    color={color || textColor}
                    fixedWidth
                    {...iconProps}
                  />
                  {href === undefined ? (
                    <Text inherit c={color || textColor}>
                      {label}
                    </Text>
                  ) : (
                    <Anchor
                      href={validateUrl(href)}
                      inherit
                      c={"gray.8"}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {label}
                    </Anchor>
                  )}
                </Group>
              </Tooltip>
            </HoverCopyButton>
          )
      )}
    </Group>
  );
};
